import React from 'react';
import styled from 'styled-components';
import { useModalStore } from '../../../shared/store/portal/modalStore';
import { useProjectElementActions, useProjectElementState } from '../../../shared/hooks/useApi/useProjectElement';
import { useProjectDetailState } from '../../../shared/hooks/useApi/useProjectDetail';
import { ProjectElementType, ModalType, AlertPosition, AlertType } from '../../../shared/enum/EnumRepository';
import { CreateProjectElementReq } from '../../../shared/dto/ReqDtoRepository';
import { peDividerBuilder, peFormationBuilder, peTextBoxBuilder } from '../../../shared/converter/entityBuilder';
import HeadlessBtn from '../../../shared/component/headless/button/HeadlessBtn';
import { PaperBlurFitBtn } from '../../../shared/component/headless/button/BtnBody';
import { getModalMessage } from '../../../shared/constants/modalMessages';
import { BULK_UPLOAD_IMAGE_MAX_COUNT, DOCUMENT_DESCRIPTION_MAX_LENGTH } from '../../../shared/constants/limits';
import { getAlertMessage } from '../../../shared/constants/alertMessages';
import { useStandardAlertStore } from '../../../shared/store/portal/alertStore';
import { useBulk } from '../../../shared/hooks/useApi/useBulk';
import { useBulkStore } from '../../../shared/store/WorkListStore';
import { getImageDimensions, removeFileExtensionWithinLimit } from '../../../shared/util/imageUtils';

const CREATE_PE_DOCUMENT_MOBILE_INPUT_ID = 'create-project-element-mobile-document-input';
const CREATE_PE_PDF_MOBILE_INPUT_ID = 'create-project-element-mobile-pdf-input';

const CreateProjectElementMobile: React.FC = () => {
  const { projectElementList } = useProjectElementState();
  const { createProjectElement, updateProjectElementBulk } = useProjectElementActions();
  const { project } = useProjectDetailState();
  const { setStandardModal } = useModalStore();
  const { enqueueAlert } = useStandardAlertStore();
  const { createPEBulk } = useBulk();
  const { setTotal, clearCurrent } = useBulkStore();

  if (!project) return null;

  const openDocumentFilePicker = () => {
    document.getElementById(CREATE_PE_DOCUMENT_MOBILE_INPUT_ID)?.click();
  };

  const handleCreatePDF =  () => {
    document.getElementById(CREATE_PE_PDF_MOBILE_INPUT_ID)?.click();
  }

  const createSingleDocumentFromFile = async (file: File) => {
    const results = await createPEBulk(
      [{ file, peType: ProjectElementType.DOCUMENT }],
      project.id,
      projectElementList.length,
    );
    if (!results?.length) return;
    const { width, height } = await getImageDimensions(file);
    await updateProjectElementBulk(project.id, {
      peList: [{
        peId: results[0].peId,
        projectElementType: ProjectElementType.DOCUMENT,
        originUrl: results[0].originUrl,
        width,
        height,
        description: removeFileExtensionWithinLimit(file.name, DOCUMENT_DESCRIPTION_MAX_LENGTH),
      }],
    });
  };

  const handleCreateDocumentFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []).filter((f) => f.type.startsWith('image/'));
    e.target.value = '';
    if (files.length === 0) return;

    if (files.length > BULK_UPLOAD_IMAGE_MAX_COUNT) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('BULK_UPLOAD_IMAGE_MAX_EXCEEDED', { maxCount: BULK_UPLOAD_IMAGE_MAX_COUNT }),
      });
      return;
    }

    if (files.length === 1) {
      createSingleDocumentFromFile(files[0]);
      return;
    }

    setTotal(files.length);
    clearCurrent();
    setStandardModal({
      modalType: ModalType.BULK_UPLOAD,
      title: 'Visual Aid',
      value: 'project-document',
      data: files,
      handleChange: () => { },
    });
  };

  const createPDFFromFiles = async (files: File[]) => {
    const results = await createPEBulk(
      files.map((file) => ({ file, peType: ProjectElementType.PDF })),
      project.id,
      projectElementList.length,
    );
    if (!results?.length) return;

    await updateProjectElementBulk(project.id, {
      peList: results.map((result, index) => ({
        peId: result.peId,
        projectElementType: ProjectElementType.PDF,
        originUrl: result.originUrl,
        fileSize: files[index]?.size,
        description: removeFileExtensionWithinLimit(files[index]?.name ?? '', DOCUMENT_DESCRIPTION_MAX_LENGTH),
      })),
    });
  };

  const handleCreatePDFFilesChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []).filter(
      (f) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf')
    );
    e.target.value = '';
    if (files.length === 0) return;

    await createPDFFromFiles(files);
  };

  // 빈 깡통 생성
  const handleCreateElement = async (elementType: ProjectElementType) => {
    let newElement: CreateProjectElementReq;
    switch (elementType) {
      case ProjectElementType.TEXTBOX:
        newElement = peTextBoxBuilder(project.id);
        break;
      case ProjectElementType.FORMATION:
        newElement = peFormationBuilder(project.id);
        break;
      case ProjectElementType.DIVIDER:
        newElement = peDividerBuilder(project.id);
        break;
      default:
        throw new Error(`Unsupported ProjectElementType: ${elementType}`);
    }
    await createProjectElement(newElement);
  };

  const handleImportWork = async () => {
    setStandardModal({
      modalType: ModalType.IMPORT_STATION_WORK,
      title: null,
      value: null,
      handleChange: () => { }
    });
  }
  const handleImportText = async () => {
    setStandardModal({
      modalType: ModalType.IMPORT_STATION_TEXT,
      title: null,
      value: null,
      handleChange: () => { }
    });
  }

  const elementButtons = [
    { 
      value: "Work", 
      handleClick: handleImportWork,
    },
    { 
      value: "Visual Aid", 
      handleClick: openDocumentFilePicker,
    },
    { 
      value: "Text", 
      handleClick: handleImportText,
    },
    { 
      value: "Description", 
      handleClick: () => handleCreateElement(ProjectElementType.TEXTBOX),
    },
    { 
      value: "PDF", 
      handleClick: handleCreatePDF,
    },
    { 
      value: "Formation", 
      handleClick: () => handleCreateElement(ProjectElementType.FORMATION),
    },
    { 
      value: "Divider", 
      handleClick: () => handleCreateElement(ProjectElementType.DIVIDER),
    },
  ];

  return (
    <CreateProjectElementComp>
      <HiddenFileInput
        id={CREATE_PE_DOCUMENT_MOBILE_INPUT_ID}
        type="file"
        accept="image/*"
        multiple
        onChange={handleCreateDocumentFilesChange}
      />
      <HiddenFileInput
        id={CREATE_PE_PDF_MOBILE_INPUT_ID}
        type="file"
        accept="application/pdf"
        onChange={handleCreatePDFFilesChange}
      />
      <TitleWrapper>
        <CreateProjectElementTitle>{getModalMessage('ADD_PROJECT_ELEMENT_MOBILE')}</CreateProjectElementTitle>         
      </TitleWrapper>
      <CreateButtonGroup $isMobile={false}>
        {elementButtons.map((button, index) => (
          <HeadlessBtn
            key={index}
            value={button.value}
            handleClick={button.handleClick}
            StyledBtn={ProjectElementOptionPaperBtn}
          />
        ))}
      </CreateButtonGroup>
    </CreateProjectElementComp>
  );
};

const CreateProjectElementComp = styled.div`
  width: 100%;
  height: fit-content;
  display: flex;
  flex-direction: column;
`

const HiddenFileInput = styled.input`
  display: none;
`;

const TitleWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  margin-top: clamp(12px, 2vh, 30px);
  margin-bottom: clamp(20px, 8vh, 30px);
`;

const CreateProjectElementTitle = styled.div`
  ${({ theme }) => theme.typography.Body_01_1};
  color: ${({ theme }) => theme.colors.color_Gray_02};
`

const CreateButtonGroup = styled.div<{ $isMobile: boolean }>`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: clamp(12px, 2vw, 16px);
`;

const ProjectElementOptionPaperBtn = styled(PaperBlurFitBtn)`
  width: 100%;
`;

export default CreateProjectElementMobile;
